'use client';

import { useGetUserExportSettingsQuery } from '@/app/(main)/client-detail/hooks/useGetUserExportSettingsQuery';
import { useGroupedClientDownloadMutation } from '@/app/(main)/client-detail/hooks/useGroupedClientDownloadMutation';
import { useUpdateUserExportSettingsMutation } from '@/app/(main)/client-detail/hooks/useUpdateUserExportSettingsMutation';
import { isGroupedExportVariant } from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { Spinner } from '@/app/components/Spinner';
import { DownloadSettingsForm } from './DownloadSettingsForm';
import { getDownloadSettingsCopy } from './consts/variantCopy';
import type { DownloadSettingsModalProps } from './types';

type DownloadSettingsModalBodyProps = {
  onClose: () => void;
  clientsId: number[];
  variant: NonNullable<DownloadSettingsModalProps['variant']>;
  scopeType?: DownloadSettingsModalProps['scopeType'];
  scopeFilters?: DownloadSettingsModalProps['scopeFilters'];
};

const DownloadSettingsModalBody = ({
  onClose,
  clientsId,
  variant,
  scopeType,
  scopeFilters,
}: DownloadSettingsModalBodyProps) => {
  const { data: exportSettings, isPending, isError, refetch } = useGetUserExportSettingsQuery();
  const { mutate: saveExportSettings, isPending: isUpdating } = useUpdateUserExportSettingsMutation();
  const { mutate: groupedDownload, isPending: isDownloading } = useGroupedClientDownloadMutation();

  if (isPending) {
    return (
      <div className="flex flex-col max-md:h-dvh max-md:w-full md:w-155.5 md:max-h-[85vh]">
        <div className="px-4 pt-8 md:hidden">
          <ModalMobileHeader title={getDownloadSettingsCopy(variant).title} onBack={onClose} />
        </div>
        <div className="flex flex-1 items-center justify-center py-24">
          <Spinner />
        </div>
      </div>
    );
  }

  if (isError || !exportSettings) {
    return (
      <div className="flex flex-col max-md:h-dvh max-md:w-full md:w-155.5 md:max-h-[85vh] items-center justify-center gap-4 px-8 py-16">
        <p className="text-center text-grey-700 font-inter">Could not load export settings.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-button-semibold text-blue-300 cursor-pointer"
        >
          Try again
        </button>
        <button type="button" onClick={onClose} className="text-button-medium text-grey-700 cursor-pointer">
          Close
        </button>
      </div>
    );
  }

  return (
    <DownloadSettingsForm
      initialExportSettings={isGroupedExportVariant(variant) ? exportSettings.groupView : exportSettings.default}
      onClose={onClose}
      clientsId={clientsId}
      variant={variant}
      scopeType={scopeType}
      scopeFilters={scopeFilters}
      saveExportSettings={saveExportSettings}
      isUpdating={isUpdating}
      groupedDownload={groupedDownload}
      isDownloading={isDownloading}
    />
  );
};

export const DownloadSettingsModal = ({
  isOpen,
  onClose,
  clientsId = [],
  variant = 'default',
  scopeType,
  scopeFilters,
}: DownloadSettingsModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <Modal open onClose={onClose} variant="fullScreenMobile">
      <DownloadSettingsModalBody
        onClose={onClose}
        clientsId={clientsId}
        variant={variant}
        scopeType={scopeType}
        scopeFilters={scopeFilters}
      />
    </Modal>
  );
};
